import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IGetAllPartnersRequestParams } from "../../domain";
import { ICreatePartnerRequestParams } from "../../domain/ICreatePartnerRequestParams";
import { IPartner } from "../../domain/IPartner";
import { partnersService } from "../../services/partner.service";
import { IPartnerPlan } from "../../domain/IPartnerPlan";
import { IPartnerPlanResponse } from "../../domain/IPartnerPlanResponse";
import { IUpdatePartnerPlanRequstParams } from "../../domain/IUpdatePartnerPlanRequstParams";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IUpdateReceivablePlanRequstParams } from "../../domain/IUpdateReceivablePlanRequstParams";
import { IPartnerReceivablesPlan } from "../../domain/IPartnerReceivablesPlan";

interface mainState {
  partners: IPartner[];
  selectedIndex: number;
  loading: boolean;
  receivablesTotalDebtAmount: number | null;
  receivablesPlanAmount: number | null;
  payablesTotalOutstandingAmount: number | null;
  payablesTotalOutstandingXxxAmount: number | null;
  payablesPlanAmount: number | null;
  searchTerm?: string;
  newPartnerPopoverOpen: boolean;
  deletePartnerModalOpen: boolean;
  searchPopover: IPopoverState;
  indexForDeletion: number;
  planPayables: IPartnerPlan[];
  planReceivables: IPartnerReceivablesPlan[];
  incidentsPopover: IPopoverState;
  error: any;
}

const initialState: mainState = {
  partners: [],
  selectedIndex: 0,
  loading: false,
  receivablesTotalDebtAmount: null,
  receivablesPlanAmount: null,
  payablesTotalOutstandingAmount: null,
  payablesTotalOutstandingXxxAmount: null,
  payablesPlanAmount: null,
  searchTerm: undefined,
  newPartnerPopoverOpen: false,
  indexForDeletion: -1,
  deletePartnerModalOpen: false,
  searchPopover: { isOpen: false, top: 0, left: 0 },
  planPayables: [],
  planReceivables: [],
  incidentsPopover: { isOpen: false },
  error: undefined,
};

export const getAllPartnersBySearchParams = createAsyncThunk(
  "Partners/getAllPartnersBySearchParams",
  async (params?: IGetAllPartnersRequestParams) => {
    try {
      return await partnersService.getAllPartners(params);
    } catch (err) {
      setError(err);
    }
  }
);

export const getAllPayablePlans = createAsyncThunk(
  "Partners/getAllPayablePlans",
  async () => {
    try {
      const response = await partnersService.getAllPayablePlans();
      return response;
    } catch (err) {
      setError(err);
    }
  }
);

export const updatePartnerPlanPayable = createAsyncThunk(
  "Partners/updatePartnerPlanPayable",
  async (params: IUpdatePartnerPlanRequstParams) => {
    try {
      let plans = await partnersService.updatePayablePlan(
        params.accountPayableId,
        params.planned
      );
      let response = { partnerId: params.partnerId, plans };
      return response;
    } catch (err) {
      throw err;
    }
  }
);

export const resetPartnerPlansPayable = createAsyncThunk(
  "Partners/resetPartnerPlansPayable",
  async () => {
    try {
      let response = await partnersService.resetPayablePlans();
      return response;
    } catch (err) {
      throw err;
    }
  }
);

export const getAllReceivablePlans = createAsyncThunk(
  "Partners/getAllReceivablePlans",
  async () => {
    try {
      const response = await partnersService.getAllReceivablePlans();
      return response;
    } catch (err) {
      setError(err);
    }
  }
);

export const updatePartnerPlanReceivable = createAsyncThunk(
  "Partners/updatePartnerPlanReceivable",
  async (params: IUpdateReceivablePlanRequstParams) => {
    try {
      let plans = await partnersService.updateReceivablePlan(
        params.accountReceivableId,
        params.planned
      );
      let response = { partnerId: params.partnerId, plans };
      return response;
    } catch (err) {
      throw err;
    }
  }
);

export const resetPartnerPlansReceivable = createAsyncThunk(
  "Partners/resetPartnerPlansReceivable",
  async () => {
    try {
      let response = await partnersService.resetReceivablePlans();
      return response;
    } catch (err) {
      throw err;
    }
  }
);

export const createPartner = createAsyncThunk(
  "Partners/createPartner",
  async (params: ICreatePartnerRequestParams, { rejectWithValue }) => {
    try {
      var cleanedParams = {
        ...params,
        phones: params.phones.split("\n"),
        bankAccount: params.bankAccount === "" ? undefined : params.bankAccount,
      };
      return await partnersService.createPartner(cleanedParams);
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const deletePartner = createAsyncThunk(
  "Partners/deletePartner",
  async (partnerId: number) => {
    try {
      return await partnersService.deletePartner(partnerId);
    } catch (err) {
      setError(err);
    }
  }
);

export const mainSlice = createSlice({
  name: "partners-main",
  initialState: initialState,
  reducers: {
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    updatePartner: (state, action: PayloadAction<IPartner>) => {
      const partner = action.payload;
      state.partners = state.partners.map((p) => {
        if (p.partnerId === partner.partnerId) return partner;
        else {
          return p;
        }
      });
    },
    updatePartnersAlarm: (
      state,
      action: PayloadAction<{ partnerId: number, hasAlarm: boolean }>) => {
      let index = state.partners.findIndex(partner =>
        partner.partnerId === action.payload.partnerId);
      state.partners[index].hasActiveAlarm = action.payload.hasAlarm;
    },
    updatePartnersHasUnacknowledgedAdditionalItems: (
      state,
      action: PayloadAction<{ partnerId: number, hasUnacknowledgedAdditionalItems: boolean }>) => {
      let index = state.partners.findIndex(partner =>
        partner.partnerId === action.payload.partnerId);
      state.partners[index].hasUnacknowledgedAdditionalItems = action.payload.hasUnacknowledgedAdditionalItems;
    },
    addPartner: (state, action: PayloadAction<IPartner>) => {
      state.partners = [action.payload, ...state.partners];
      state.selectedIndex = 0;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    setSelectedIndexById: (state, action: PayloadAction<number>) => {
      state.selectedIndex = state.partners
        .map((x) => {
          return x.partnerId;
        })
        .indexOf(action.payload);
    },
    setNewPartnerPopoverOpen: (state, action: PayloadAction<boolean>) => {
      state.newPartnerPopoverOpen = action.payload;
    },
    setDeletePartnerModalOpen: (state, action: PayloadAction<boolean>) => {
      state.deletePartnerModalOpen = action.payload;
    },
    setSearchPopoverState: (state, action: PayloadAction<IPopoverState>) => {
      state.searchPopover = action.payload;
    },
    setIndexForDeletion: (state, action: PayloadAction<number>) => {
      state.indexForDeletion = action.payload;
      state.deletePartnerModalOpen = action.payload !== -1;
    },
    setPayablePlans: (state, action: PayloadAction<IPartnerPlanResponse>) => {
      state.planPayables = action.payload.partnerPlans;
    },
    removePartner: (state, action: PayloadAction<number>) => {
      state.partners = state.partners.filter(
        (p) => p.partnerId !== action.payload
      );
    },
    setIncidentPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.incidentsPopover = action.payload;
    },
    setPartnerHasAlarm: (state, action: PayloadAction<boolean>) => {
      state.partners[state.selectedIndex].hasActiveAlarm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllPartnersBySearchParams.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllPartnersBySearchParams.fulfilled, (state, action) => {
      if (action.payload) {
        state.loading = false;
        state.partners = action.payload.partners;
        state.payablesTotalOutstandingAmount =
          action.payload.payablesTotalOutstandingAmount;
        state.payablesTotalOutstandingXxxAmount =
          action.payload.payablesTotalOutstandingXxxAmount;
        state.receivablesTotalDebtAmount =
          action.payload.receivablesTotalDebtAmount;
        state.payablesPlanAmount = action.payload.payablesTotalPlanAmount;
        state.receivablesPlanAmount = action.payload.receivablesTotalPlanAmount;
      }
    });
    builder.addCase(getAllPartnersBySearchParams.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getAllPayablePlans.fulfilled, (state, action) => {
      if (action.payload) {
        state.planPayables = action.payload;
      }
    });
    builder.addCase(updatePartnerPlanPayable.fulfilled, (state, action) => {
      state.partners.forEach((element) => {
        if (element.partnerId === action.payload.partnerId) {
          element.payablesPlanAmount =
            action.payload.plans.payablesTotalPlanAmountForPartner;
        }
      });
      state.payablesPlanAmount =
        action.payload.plans.payablesTotalPlanAmountForAllPartners;
    });
    builder.addCase(resetPartnerPlansPayable.fulfilled, (state, action) => {
      if (action.payload) {
        state.partners.forEach((element) => {
          element.payablesPlanAmount = 0;
        });
        state.payablesPlanAmount = 0;
      }
    });
    builder.addCase(getAllReceivablePlans.fulfilled, (state, action) => {
      if (action.payload) {
        state.planReceivables = action.payload;
      }
    });
    builder.addCase(updatePartnerPlanReceivable.fulfilled, (state, action) => {
      state.partners.forEach((element) => {
        if (element.partnerId === action.payload.partnerId) {
          element.receivablesPlanAmount =
            action.payload.plans.receivablesTotalPlanAmountForPartner;
        }
      });
      state.receivablesPlanAmount =
        action.payload.plans.receivablesTotalPlanAmountForAllPartners;
    });
    builder.addCase(resetPartnerPlansReceivable.fulfilled, (state, action) => {
      if (action.payload) {
        state.partners.forEach((element) => {
          element.receivablesPlanAmount = 0;
        });
        state.receivablesPlanAmount = 0;
      }
    });
  },
});

export const {
  setNewPartnerPopoverOpen,
  setError,
  updatePartner,
  updatePartnersAlarm,
  updatePartnersHasUnacknowledgedAdditionalItems,
  addPartner,
  setSelectedIndex,
  setSelectedIndexById,
  setSearchTerm,
  setIndexForDeletion,
  setDeletePartnerModalOpen,
  setSearchPopoverState,
  removePartner,
  setIncidentPopover,
  setPartnerHasAlarm,
} = mainSlice.actions;

export const selectState = (state: RootState) => state.partners.main;
export const selectPartner = (state: RootState) =>
  state.partners.main.partners[state.partners.main.selectedIndex];
export const selectPartnerForDeletion = (state: RootState) =>
  state.partners.main.partners[state.partners.main.indexForDeletion];
export default mainSlice.reducer;
